import React, { useState } from 'react';
import { Box, Typography, Button, useTheme, useMediaQuery } from '@mui/material';
import { MessageSquarePlus } from 'lucide-react';
import { MessageLayout } from '../components/messages/MessageLayout';
import { MessageSearch } from '../components/messages/MessageSearch';
import { ConversationList } from '../components/messages/ConversationList';
import { MessageThread } from '../components/messages/MessageThread';
import { NewMessageDialog } from '../components/messages/NewMessageDialog';
import { useMessageStore } from '../store/messageStore';
import { cases } from '../data/sampleData';

export const Messages: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { 
    conversations, 
    messages,
    activeConversation,
    setActiveConversation,
    sendMessage,
    markAsRead,
    startConversation,
  } = useMessageStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [newMessageDialogOpen, setNewMessageDialogOpen] = useState(false);

  // Filter conversations based on case number or title
  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    
    const searchLower = searchQuery.toLowerCase();
    const caseDetails = cases.find(c => c.id === conv.caseId);
    
    return caseDetails && (
      caseDetails.caseNumber.toLowerCase().includes(searchLower) ||
      caseDetails.title.toLowerCase().includes(searchLower)
    );
  });

  const handleStartConversation = (participantIds: string[], caseId: string) => {
    const newConversationId = startConversation(participantIds, caseId);
    setActiveConversation(newConversationId);
  };

  const handleConversationSelect = (conversationId: string) => {
    setActiveConversation(conversationId);
  };

  const handleBack = () => {
    setActiveConversation(null);
  };

  // Show either the conversation list or the message thread on mobile
  const showMessageThread = isMobile && activeConversation;
  const showConversationList = !isMobile || !activeConversation;

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        gap: 2,
        flexWrap: 'wrap',
      }}>
        <Typography variant="h4">Messages</Typography>
        <Button
          variant="contained"
          startIcon={<MessageSquarePlus />}
          onClick={() => setNewMessageDialogOpen(true)}
          sx={{ 
            whiteSpace: 'nowrap',
            minWidth: { xs: '100%', sm: 'auto' },
          }}
        >
          New Message
        </Button>
      </Box>

      <MessageLayout 
        showBackButton={showMessageThread}
        onBack={handleBack}
      >
        {showConversationList && (
          <MessageLayout.Sidebar>
            <MessageSearch 
              value={searchQuery}
              onChange={setSearchQuery}
            />
            <ConversationList
              conversations={filteredConversations}
              activeConversation={activeConversation}
              onConversationSelect={handleConversationSelect}
            />
          </MessageLayout.Sidebar>
        )}

        {(showMessageThread || !isMobile) && (
          <MessageLayout.Content>
            <MessageThread
              conversation={conversations.find(c => c.id === activeConversation)}
              messages={activeConversation ? messages[activeConversation] : []}
              onSendMessage={sendMessage}
            />
          </MessageLayout.Content>
        )}
      </MessageLayout>

      <NewMessageDialog
        open={newMessageDialogOpen}
        onClose={() => setNewMessageDialogOpen(false)}
        onStartConversation={handleStartConversation}
      />
    </Box>
  );
};